/**
 * Action Center Premium (0666b) — orquestador.
 *
 * Reemplaza al monolito ActionCenterPage.tsx (1425 LoC) por un componente
 * delgado que compone:
 *   - ActionCenterToolbar (search + tabs + count)
 *   - ActionCenterTable (virtualizada · 36px row · sticky header)
 *   - ActionCenterDetailPanel (3 cols · footer fijo cancelar/rechazar/aprobar)
 *
 * Estado local mínimo: filtros (q, tipo) + selectedId. Datos vía React Query.
 *
 * Mantiene la ruta `/sign/action-center` — el componente legacy queda como
 * referencia hasta confirmar que no rompe el flujo (T-9 sustitución de ruta).
 */

import { useEffect, useMemo, useState } from 'react';
import { ActionCenterToolbar } from './components/ActionCenterToolbar';
import { ActionCenterTable } from './components/ActionCenterTable';
import { ActionCenterDetailPanel } from './components/ActionCenterDetailPanel';
import { ActionCenterPagination } from './components/ActionCenterPagination';
import {
  useActionCenterList,
  type ActionItem,
  type ActionItemTipo,
  type VistaActionCenter,
} from './hooks/useActionCenterList';

// 0669 — persistencia de vista + pageSize en localStorage.
const LS_VISTA = 'vianet-action-center-vista';
const LS_PAGE_SIZE = 'vianet-action-center-pageSize';

function readVista(): VistaActionCenter {
  const v = localStorage.getItem(LS_VISTA);
  if (v === 'PENDIENTES' || v === 'EN_CURSO' || v === 'HISTORICO') return v;
  return 'PENDIENTES';
}

function readPageSize(): number {
  const n = Number(localStorage.getItem(LS_PAGE_SIZE));
  if ([10, 25, 50, 100].includes(n)) return n;
  return 25;
}

export function ActionCenterPremium() {
  const [vista, setVista] = useState<VistaActionCenter>(readVista);
  const [q, setQ] = useState('');
  const [tipo, setTipo] = useState<ActionItemTipo | 'all'>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(readPageSize);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Persistir vista + pageSize tras cambio.
  useEffect(() => {
    localStorage.setItem(LS_VISTA, vista);
  }, [vista]);
  useEffect(() => {
    localStorage.setItem(LS_PAGE_SIZE, String(pageSize));
  }, [pageSize]);

  // Reset a página 1 cuando cambia vista/tipo/q (cambio de dataset).
  useEffect(() => {
    setPage(1);
  }, [vista, tipo, q, pageSize]);

  const { data, isLoading, isError, refetch } = useActionCenterList({
    vista,
    q,
    tipo,
    page,
    pageSize,
  });

  const items = useMemo(() => data?.data ?? [], [data]);
  const meta = data?.meta;
  const counts = useMemo(
    () =>
      meta?.counts ?? {
        ORGANIGRAMA: 0,
        DOC: 0,
        FLUJOGRAMA: 0,
        APROBACION_DOCUMENTO: 0,
        APROBACION_PETICION: 0,
        APROBACION_COMPRA: 0,
      },
    [meta],
  );

  const selected = useMemo(
    () => (selectedId ? (items.find((it) => it.id === selectedId) ?? null) : null),
    [items, selectedId],
  );

  const handleSelect = (item: ActionItem) => {
    setSelectedId(item.id);
  };

  const handleClose = () => {
    setSelectedId(null);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 480,
        background: '#fff',
      }}
    >
      <ActionCenterToolbar
        vista={vista}
        onVistaChange={setVista}
        q={q}
        onQChange={setQ}
        tipo={tipo}
        onTipoChange={setTipo}
        counts={counts}
        total={meta?.total ?? 0}
      />
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <ActionCenterTable
          items={items}
          selectedId={selectedId}
          onSelect={handleSelect}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => {
            void refetch();
          }}
        />
      </div>
      <ActionCenterPagination
        page={page}
        pageSize={pageSize}
        total={meta?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
      {selected && <ActionCenterDetailPanel item={selected} onClose={handleClose} />}
    </div>
  );
}

export default ActionCenterPremium;
